import { type Dispatch, type ReactElement, type SetStateAction, useMemo, useState } from 'react'
import {
  isEmpty,
  isNotDefined,
  isNotEmpty,
  type LocationExchangeUnit,
  type LocationId,
  type SmallAddressWithCountry,
  type WithOptionalLocationId,
} from '@terros/common'
import { useDisclosure } from '@mantine/hooks'
import { useForm } from '@mantine/form'
import { Combobox, Loader, Modal, rem, Stack, TextInput, useCombobox } from '@mantine/core'
import { useToastError } from '@hooks'
import {
  Size,
  type PlaceDetailsWithUnits,
  canEditAddressDetails,
  isUnitRequired,
  useAddLocation,
  useAutocompleteBox,
  useBlocksCustomLocationDetailEdits,
  useLocation,
  useMatchLocation,
  useProfile,
  useTranslation,
} from '@front/model'
import { MaterialSymbolIcon } from '../terros/icon/MaterialSymbolIcon'
import { TAlert } from '../terros'
import { ButtonSave, type TButtonProps } from '../buttons'
import { UnitSelect } from './UnitSelect'
import { PickLocation } from './PickLocation'

type LocationValues = {
  address: SmallAddressWithCountry
  locationId?: LocationId
}

type WithOnLocationChange = {
  onLocationChange: (values: { address: SmallAddressWithCountry; locationId?: LocationId }) => void
}

type LocationAutocompleteProps = WithOnLocationChange &
  WithOptionalLocationId & {
    buttonProps?: Omit<TButtonProps, 'onClick' | 'children'>
    disabled?: boolean
    defaultOpen?: boolean
    canEditDetails?: boolean
  }

export const LocationAutocomplete = (props: LocationAutocompleteProps): ReactElement => {
  const { locationId, disabled, defaultOpen, buttonProps } = props
  const [isOpen, { open, close }] = useDisclosure(defaultOpen)
  const { data, isLoading } = useLocation(locationId, { extended: false })
  const { t } = useTranslation()

  return (
    <>
      <PickLocation {...buttonProps} disabled={disabled} locationId={locationId} onClick={open} />
      <Modal opened={isOpen} title={t('Address Details')} onClose={close}>
        {!isLoading && <LocationAutocompleteModal {...props} address={data} close={close} />}
        {isLoading && <Loader />}
      </Modal>
    </>
  )
}

export const LocationAutocompleteModal = ({
  locationId,
  onLocationChange: onLocationSave,
  close: closeModal,
  address,
  canEditDetails = true,
}: LocationAutocompleteProps & {
  close: VoidFunction
  address?: SmallAddressWithCountry
}): ReactElement => {
  const [isBusy, setIsBusy] = useState(false)
  const [units, setUnits] = useState<LocationExchangeUnit[]>([])
  const { user } = useProfile()
  const { add, isAdding, error } = useAddLocation()
  const { match, isLoading, error: matchError } = useMatchLocation()
  const { t } = useTranslation()
  const { blocksEdits, isLoading: isCheckingBlock } = useBlocksCustomLocationDetailEdits()
  const detailsEditable = canEditAddressDetails({
    allowDetailEdits: canEditDetails,
    blocksEdits,
    isCheckingBlock,
  })

  useToastError(matchError)
  useToastError(error)

  const { getInputProps, setValues, values, isDirty, resetDirty } = useForm<LocationValues>({
    initialValues: {
      address: address ?? {
        line1: '',
        locality: '',
        countrySubd: '',
        postal1: '',
        country: 'US',
        latlng: { latitude: 0, longitude: 0 },
      },
      locationId,
    },
    validate: {
      address: {
        line1: (value) => (value ? undefined : t('Address is required')),
        locality: (value) => (value ? undefined : t('City is required')),
        countrySubd: (value) => (value ? undefined : t('State is required')),
        postal1: (value) => (value ? undefined : t('ZIP Code is required')),
      },
    },
  })

  const validAddress = useMemo(() => isAddressValid(values.address), [values.address])
  const unitRequired = isUnitRequired({
    units,
    locationId: values.locationId,
    unitNbr: values.address.unitNbr,
  })
  const isNewAddress = validAddress && (isDirty() || isNotDefined(values.locationId))

  const handleFormSubmit = async (): Promise<void> => {
    if (!user?.companyId) return

    if (isNewAddress) {
      const matchResult = await match({
        addressParts: values.address,
      })
      if (matchResult.location) {
        onLocationSave({ ...values, locationId: matchResult.location.locationId })
      } else {
        const newLocation = await add(values.address)
        onLocationSave({ ...values, locationId: newLocation.locationId })
      }
    } else {
      onLocationSave(values)
    }
    closeModal()
  }

  return (
    <Stack gap={Size.space['2xl']}>
      <LocationInput
        isBusy={isBusy}
        setIsBusy={setIsBusy}
        onPlaceSelect={(place) => {
          setValues({ address: place.details, locationId: place.details.locationId })
          setUnits(place.units)
          resetDirty()
        }}
      />
      {isNotEmpty(units) && (
        <UnitSelect
          locationId={values.locationId}
          required={unitRequired}
          units={units}
          onUnitChange={(unit) => {
            setValues({ address: unit.details, locationId: unit.details.locationId })
            resetDirty()
          }}
        />
      )}
      {blocksEdits && (
        <TAlert variant='warning'>{t('Address details cannot be edited. Search for an address instead.')}</TAlert>
      )}
      <TextInput {...getInputProps('address.line1')} label={t('Line 1')} disabled={!detailsEditable} />
      <TextInput {...getInputProps('address.unitNbr')} label={t('Unit Number')} disabled={!detailsEditable} />
      <TextInput {...getInputProps('address.unitType')} label={t('Unit Type')} disabled={!detailsEditable} />
      <TextInput {...getInputProps('address.locality')} label={t('City')} disabled={!detailsEditable} />
      <TextInput {...getInputProps('address.countrySubd')} label={t('State')} disabled={!detailsEditable} />
      <TextInput {...getInputProps('address.postal1')} label={t('ZIP Code')} disabled={!detailsEditable} />
      <ButtonSave
        loading={isAdding || isLoading}
        disabled={isBusy || !validAddress || unitRequired}
        onClick={handleFormSubmit}
      >
        {t('Update Location')}
      </ButtonSave>
    </Stack>
  )
}

type LocationInputProps = {
  isBusy: boolean
  setIsBusy: Dispatch<SetStateAction<boolean>>
  onPlaceSelect: (place: PlaceDetailsWithUnits) => void
}
const LocationInput = ({ onPlaceSelect, isBusy, setIsBusy }: LocationInputProps): ReactElement => {
  const [search, setSearch] = useState<string>('')
  const { data, getDetailsWithUnits, isLoading } = useAutocompleteBox({ search, setSearch })
  const { t } = useTranslation()

  const [isOpened, { open, close }] = useDisclosure()
  const comboboxStore = useCombobox({
    onDropdownClose: () => {
      comboboxStore.resetSelectedOption()
      close()
    },
    opened: isOpened,
  })

  return (
    <Combobox
      store={comboboxStore}
      onOptionSubmit={async (placeId) => {
        try {
          setIsBusy(true)
          comboboxStore.closeDropdown()
          onPlaceSelect(await getDetailsWithUnits(placeId))
        } finally {
          setIsBusy(false)
        }
      }}
    >
      <Combobox.Target>
        <TextInput
          className='min-w-80'
          leftSection={<MaterialSymbolIcon icon='search' size={14} />}
          placeholder={t('Search for an address')}
          rightSection={isBusy || isLoading ? <Loader size={14} /> : undefined}
          value={search}
          onBlur={() => comboboxStore.closeDropdown()}
          onChange={(e) => {
            open()
            setSearch(e.currentTarget.value)
          }}
          autoFocus
        />
      </Combobox.Target>
      <Combobox.Dropdown>
        <Combobox.Options mah={rem(400)} style={{ overflowY: 'auto' }}>
          {!isLoading &&
            data.map((p) => (
              <Combobox.Option key={p.placeId} value={p.placeId}>
                {p.text}
              </Combobox.Option>
            ))}
          {isEmpty(data) && isEmpty(search) && <Combobox.Empty>{t('Start typing to search')}</Combobox.Empty>}
        </Combobox.Options>
      </Combobox.Dropdown>
    </Combobox>
  )
}

const isAddressValid = (address: SmallAddressWithCountry): boolean => {
  return (
    isNotEmpty(address.line1) &&
    isNotEmpty(address.locality) &&
    isNotEmpty(address.countrySubd) &&
    isNotEmpty(address.postal1)
  )
}
